import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Unlock, Lock, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OpenAccessCard = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState(14);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/open-access`, { withCredentials: true });
      setState(data);
    } catch (e) {
      setState({ enabled: false, until: null, active: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (enabled) => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/open-access`,
        { enabled, days: Number(days) || 14 },
        { withCredentials: true }
      );
      setState(data);
      toast.success(enabled ? `Open access ON — ends ${fmt(data.until)}` : 'Open access turned OFF — login now required');
    } catch (e) {
      toast.error('Could not update open access');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  const active = state?.active;

  return (
    <div
      data-testid="open-access-card"
      className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${active ? 'bg-emerald-500/20' : 'bg-[#06B6D4]/20'}`}>
            {active ? <Unlock className="w-5 h-5 text-emerald-400" /> : <Lock className="w-5 h-5 text-[#06B6D4]" />}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Open Access Window</h3>
            <p className="text-sm text-zinc-400">
              Let anyone use all features with no sign-in. Admin area always stays protected.
            </p>
          </div>
        </div>
        <span
          data-testid="open-access-status-badge"
          className={`text-xs font-semibold px-3 py-1 rounded-full ${active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700/40 text-zinc-400'}`}
        >
          {loading ? '…' : active ? 'ACTIVE' : 'OFF'}
        </span>
      </div>

      {!loading && (
        <div className="mt-4 space-y-3">
          {active && (
            <p className="text-sm text-zinc-300" data-testid="open-access-until">
              Ends <span className="text-white font-medium">{fmt(state.until)}</span>
              {daysLeft(state.until) != null && (
                <span className="text-zinc-500"> ({daysLeft(state.until)} day{daysLeft(state.until) === 1 ? '' : 's'} left)</span>
              )}
            </p>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-zinc-400">Duration</label>
            <input
              type="number"
              min={1}
              max={90}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              disabled={saving}
              data-testid="open-access-days-input"
              className="w-20 bg-[#050505] border border-[#1E1E24] rounded-md px-2 py-1 text-sm text-white"
            />
            <span className="text-sm text-zinc-400">days</span>

            <button
              onClick={() => save(true)}
              disabled={saving}
              data-testid="open-access-enable-btn"
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
              {active ? 'Extend / Restart' : 'Open access'}
            </button>
            <button
              onClick={() => save(false)}
              disabled={saving || !active}
              data-testid="open-access-disable-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-40"
            >
              <Lock className="w-4 h-4" />
              Restore login
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpenAccessCard;
